import { EditorContent } from '@tiptap/react';
import { GitBranch, Pilcrow, Plus, ShieldCheck, X } from 'lucide-react';
import { cn } from '@design-system';
import type { DocumentBlock, ReplyCatalog } from '../api/api';
import { useEmailEditor } from '../hooks/use-email-editor';

const GROUPS = [
  { group: 'client', label: 'The client' },
  { group: 'general', label: 'General' },
] as const;

const PLACEHOLDER_STYLES =
  '[&_.is-empty]:before:pointer-events-none [&_.is-empty]:before:float-left [&_.is-empty]:before:h-0 [&_.is-empty]:before:text-zinc-400 [&_.is-empty]:before:content-[attr(data-placeholder)]';

function AddButton({
  icon,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 items-center gap-1.5 rounded-[10px] border border-dashed border-zinc-400 bg-white px-3.5 text-sm font-medium text-zinc-700 hover:border-[#5B3FD6] hover:text-[#4527B8]"
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * The information chips and the email they go into. A chip is added by clicking it (to where the
 * cursor is) or by dragging it into the email.
 */
export function EmailComposer(props: {
  resetKey: string;
  catalog: ReplyCatalog;
  document: DocumentBlock[];
  onChange: (document: DocumentBlock[]) => void;
}) {
  const email = useEmailEditor({
    resetKey: props.resetKey,
    catalog: props.catalog,
    initial: props.document,
    onChange: props.onChange,
  });
  const offered = props.catalog.fields.filter(field => field.offered && field.kind === 'code');

  return (
    <>
      <section className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">Information you can add</h2>
          <span className="text-[13px] text-zinc-600">
            {email.area ? (
              <>
                Click to add it to{' '}
                <strong className="font-semibold text-zinc-900">{email.area}</strong>, or drag it
                into the email
              </>
            ) : (
              'Legal notes are sent word for word: click elsewhere in the email to add information'
            )}
          </span>
        </div>
        <div className="flex flex-col gap-2.5">
          {GROUPS.map(({ group, label }) => (
            <div
              key={group}
              className="flex flex-wrap items-center gap-2"
            >
              <span className="w-[88px] text-[13px] text-zinc-600">{label}</span>
              {offered
                .filter(field => field.group === group)
                .map(field => (
                  <button
                    key={field.name}
                    type="button"
                    title={field.meaning ?? undefined}
                    onClick={() => email.insertField(field.name)}
                    {...email.chipDragHandlers(field.name)}
                    className="inline-flex h-11 cursor-grab items-center gap-1.5 rounded-full border border-[#D9D0FB] bg-[#F6F3FF] pl-2.5 pr-3.5 text-sm font-semibold text-[#3B1FA8] hover:border-[#5B3FD6] active:cursor-grabbing"
                  >
                    <Plus
                      className="size-3.5"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                    {field.label}
                  </button>
                ))}
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3.5 rounded-xl border border-zinc-200 bg-white px-5 pb-5 pt-4">
        <h2 className="text-base font-semibold">The email</h2>
        {email.notice && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-lg bg-[#FFF4E5] px-3 py-2 text-[13px] text-[#8A4B00]"
          >
            {email.notice}
            <button
              type="button"
              aria-label="Dismiss"
              onClick={email.dismissNotice}
              className="ml-auto"
            >
              <X
                className="size-3.5"
                aria-hidden
              />
            </button>
          </div>
        )}
        <div
          className={cn(
            'rounded-[10px] border px-3.5 py-2 transition-colors',
            email.dragging
              ? 'border-2 border-dashed border-[#5B3FD6] bg-[#FBFAFF]'
              : 'border-zinc-200 focus-within:border-[#5B3FD6]',
            PLACEHOLDER_STYLES,
          )}
        >
          <EditorContent editor={email.editor} />
        </div>
        <div className="flex flex-wrap gap-2">
          <AddButton
            icon={
              <Pilcrow
                className="size-3.5"
                aria-hidden
              />
            }
            onClick={() => email.addBlock('paragraph')}
          >
            Paragraph
          </AddButton>
          <AddButton
            icon={
              <GitBranch
                className="size-3.5"
                aria-hidden
              />
            }
            onClick={() => email.addBlock('condition')}
          >
            Part that changes
          </AddButton>
          <AddButton
            icon={
              <ShieldCheck
                className="size-3.5"
                aria-hidden
              />
            }
            onClick={() => email.addBlock('legal')}
          >
            Legal note
          </AddButton>
        </div>
      </section>
    </>
  );
}
